import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

export default class extends Controller {
  static values = {
    allowEmptyOption: Boolean,
    attachedToBody: Boolean,
    fixModalSelection: Boolean,
    dropdownHeader: String,
    maxItems: Number,
    create: Boolean,
    createFilter: String
  }

  initialize() {
    this.tomSelectOptions = {
      maxOptions: null
    }
  }

  connect() {
    this.initializeTomSelect()

    this.element["select"] = this

    if (this.element.multiple && this.element.name && !this.element.name.endsWith("[]")) {
      this.element.name += "[]"
    }

    if (this.element.hasAttribute("readonly")) {
      this.tomSelect.disable()
      this.element.removeAttribute("disabled")
    }
  }

  disconnect() {
    this.destroyTomSelect()
  }

  initializeTomSelect() {
    const options = {
      ...this.defaultTomSelectOptions,
      ...this.tomSelectOptions
    }

    this.tomSelect = new TomSelect(this.element, options)

    // TomSelect copies classes from the original <select> to .ts-wrapper — remove cocoon-field from it.
    const wrapper = this.tomSelect.wrapper
    if (wrapper) wrapper.classList.remove("cocoon-field")
  }

  destroyTomSelect() {
    if (!this.tomSelect) return

    this.tomSelect.destroy()
  }

  onChange() {
    this.dispatch("change", { prefix: false })
  }

  onInitialize() {
    this.dispatch("initialized")
  }

  get defaultTomSelectOptions() {
    const opts = {
      onInitialize: this.onInitialize.bind(this),
      onChange: this.onChange.bind(this),
      allowEmptyOption: this.allowEmptyOptionValue,
      create: this.createValue ? this.handleCreate.bind(this) : false,
      createFilter: this.createFilterValue,
      plugins: {},
      render: {
        option: (data, escape) => {
          const skipKeys = new Set(["text", "value", "disabled", "$order"])
          const dataAttributes = Object.keys(data)
            .filter(key => !skipKeys.has(key))
            .map(key => `data-${key}="${escape(data[key])}"`)
            .join(" ")
          return `<div class="option" ${dataAttributes}>${escape(data.text)}</div>`
        }
      }
    }

    if (this.hasMaxItemsValue) opts.maxItems = this.maxItemsValue

    if (this.element.multiple && this.maxItemsValue != 1) {
      opts.plugins.remove_button = {}
    }

    if (this.dropdownHeaderValue.length) {
      opts.plugins.dropdown_header = {
        title: this.dropdownHeaderValue,
        closeClass: "hidden"
      }
    }

    if (this.attachedToBodyValue) opts.dropdownParent = "body"

    return opts
  }

  handleCreate(input, callback) {
    const event = this.dispatch("create", { detail: { input, callback } })
    if (!event.defaultPrevented) { // there was no create handler
      return { value: input, text: input }
    }
  }

  setValue(value) {
    this.tomSelect.setValue(value)
  }

  replaceOptions(options) {
    this.clearOptions()
    this.tomSelect.addOptions(options)
    this.tomSelect.refreshOptions(false)
  }

  hideOptions(values) {
    this.tomSelect.refreshOptions(false)
    this.optionElements.forEach(option => {
      option.hidden = values.includes(option.dataset.value)
    })
  }

  clearOptions() {
    this.clear()
    this.tomSelect.clearOptions()
  }

  clear() {
    this.tomSelect.clear()
  }

  disable() {
    this.tomSelect.disable()
  }

  enable() {
    this.tomSelect.enable()
  }

  get optionElements() {
    return Array.from(this.tomSelect.dropdown_content.querySelectorAll(".option"))
  }
}
